/**
 * HMS View Contracts
 *
 * Canonical DSL column definitions, field definitions, and list/detail/form
 * templates for HMS (Container Trucking) entities.
 *
 * These follow the same pattern as finance-view-contracts.ts — they are the
 * authoritative source of truth for how HMS data is displayed.
 *
 * Import from here when you need HMS column sets or form field lists.
 * The fallback-view-templates.ts file re-exports from here so existing
 * template-resolution code continues to work.
 */

import type { ViewTemplateDSL } from '@/lib/use-view-template';

// ─────────────────────────────────────────────────────────────────────────────
// List Column Sets
// ─────────────────────────────────────────────────────────────────────────────

export const HMS_JOB_LIST_COLUMNS: NonNullable<ViewTemplateDSL['columns']> = [
  { field: 'jobNumber', label: 'Job No', width: 130, sortable: true, pinned: 'left' },
  { field: 'jobType', label: 'Type', width: 90, format: 'status' },
  { field: 'status', label: 'Status', width: 110, format: 'status', sortable: true },
  { field: 'containerNo', label: 'Container', width: 140 },
  { field: 'containerSize', label: 'Size', width: 70 },
  { field: 'customerName', label: 'Customer', width: 160 },
  { field: 'blNo', label: 'B/L No', width: 150 },
  { field: 'vessel', label: 'Vessel', width: 120 },
  { field: 'terminal', label: 'Terminal', width: 100 },
  { field: 'lfd', label: 'LFD', width: 100, format: 'date', sortable: true },
  { field: 'eta', label: 'ETA', width: 100, format: 'date', sortable: true },
  { field: 'driverName', label: 'Driver', width: 120 },
  { field: 'driverPlate', label: 'Vehicle', width: 100 },
  { field: 'pickupDate', label: 'Pickup', width: 110, format: 'date' },
  { field: 'deliveryDate', label: 'Delivery', width: 110, format: 'date' },
  { field: 'createdAt', label: 'Created', width: 150, format: 'datetime', pinned: 'right' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Form Field Definitions
// ─────────────────────────────────────────────────────────────────────────────

export const HMS_JOB_FORM_FIELDS: NonNullable<ViewTemplateDSL['fields']> = [
  { field: 'jobNumber', label: 'Job No', required: true, group: 'Header' },
  { field: 'jobType', label: 'Job Type', required: true, type: 'select', group: 'Header', options: [
    { label: 'Import (IMP+RET)', value: 'import' },
    { label: 'Export (COL+EXP)', value: 'export' },
  ]},
  { field: 'status', label: 'Status', type: 'select', group: 'Header' },
  { field: 'containerNo', label: 'Container No', required: true, group: 'Container' },
  { field: 'containerSize', label: 'Container Size', type: 'select', group: 'Container' },
  { field: 'sealNo', label: 'Seal No', group: 'Container' },
  { field: 'terminal', label: 'Terminal', group: 'Container' },
  { field: 'customerName', label: 'Customer', type: 'lookup', group: 'Parties' },
  { field: 'customerRef', label: 'Customer Ref', group: 'Parties' },
  { field: 'doNo', label: 'D/O No', group: 'References' },
  { field: 'blNo', label: 'B/L No', group: 'References' },
  { field: 'vessel', label: 'Vessel', group: 'Vessel' },
  { field: 'voyage', label: 'Voyage', group: 'Vessel' },
  { field: 'origin', label: 'Origin', group: 'Routing' },
  { field: 'destination', label: 'Destination', group: 'Routing' },
  { field: 'eta', label: 'ETA', type: 'date', group: 'Dates' },
  { field: 'etd', label: 'ETD', type: 'date', group: 'Dates' },
  { field: 'lfd', label: 'Last Free Date', type: 'date', group: 'Dates' },
  { field: 'pickupDate', label: 'Pickup Date', type: 'date', group: 'Dates' },
  { field: 'deliveryDate', label: 'Delivery Date', type: 'date', group: 'Dates' },
  { field: 'deliveryAddress', label: 'Delivery Address', type: 'textarea', span: 2, group: 'Delivery' },
  { field: 'driverName', label: 'Driver', type: 'lookup', group: 'Assignment' },
  { field: 'driverPlate', label: 'Vehicle', type: 'lookup', group: 'Assignment' },
  { field: 'cargoDesc', label: 'Cargo Description', type: 'textarea', span: 2, group: 'Cargo' },
  { field: 'cargoWeightKg', label: 'Weight (kg)', type: 'number', group: 'Cargo' },
  { field: 'hsCode', label: 'HS Code', group: 'Cargo' },
  { field: 'dgClass', label: 'DG Class', group: 'Cargo' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Detail Sections (for record detail view)
// ─────────────────────────────────────────────────────────────────────────────

export const HMS_JOB_DETAIL_SECTIONS: NonNullable<ViewTemplateDSL['sections']> = [
  {
    title: 'Container & Terminal',
    columns: 2,
    fields: [
      { field: 'containerNo', label: 'Container No' },
      { field: 'containerSize', label: 'Size' },
      { field: 'sealNo', label: 'Seal No' },
      { field: 'terminal', label: 'Terminal' },
    ],
  },
  {
    title: 'References',
    columns: 2,
    fields: [
      { field: 'jobNumber', label: 'Job No' },
      { field: 'jobType', label: 'Type' },
      { field: 'doNo', label: 'D/O No' },
      { field: 'blNo', label: 'B/L No' },
      { field: 'customerName', label: 'Customer' },
      { field: 'customerRef', label: 'Customer Ref' },
    ],
  },
  {
    title: 'Vessel & Route',
    columns: 2,
    fields: [
      { field: 'vessel', label: 'Vessel' },
      { field: 'voyage', label: 'Voyage' },
      { field: 'origin', label: 'Origin' },
      { field: 'destination', label: 'Destination' },
      { field: 'eta', label: 'ETA' },
      { field: 'etd', label: 'ETD' },
    ],
  },
  {
    title: 'Critical Dates',
    columns: 2,
    fields: [
      { field: 'lfd', label: 'Last Free Date (LFD)' },
      { field: 'dischargeDate', label: 'Discharge' },
      { field: 'availableDate', label: 'Available' },
      { field: 'pickupDate', label: 'Pickup' },
      { field: 'deliveryDate', label: 'Delivery' },
    ],
  },
  {
    title: 'Assignment',
    columns: 2,
    fields: [
      { field: 'driverName', label: 'Driver' },
      { field: 'driverPhone', label: 'Driver Phone' },
      { field: 'driverPlate', label: 'Vehicle' },
      { field: 'dispatcher', label: 'Dispatcher' },
    ],
  },
  {
    title: 'Cargo',
    columns: 2,
    fields: [
      { field: 'cargoDesc', label: 'Description', span: 2 },
      { field: 'cargoWeightKg', label: 'Weight (kg)' },
      { field: 'hsCode', label: 'HS Code' },
      { field: 'dgClass', label: 'DG Class' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Intake Cases List Template
// ─────────────────────────────────────────────────────────────────────────────

export const INTAKE_CASES_LIST_COLUMNS: NonNullable<ViewTemplateDSL['columns']> = [
  { field: 'caseType', label: 'Type', width: 100, format: 'status' },
  { field: 'intentType', label: 'Intent', width: 120 },
  { field: 'status', label: 'Status', width: 110, format: 'status' },
  { field: 'subject', label: 'Subject', width: 260 },
  { field: 'senderName', label: 'Sender', width: 140 },
  { field: 'senderEmail', label: 'Email', width: 180 },
  { field: 'channelType', label: 'Channel', width: 90, format: 'status' },
  { field: 'confidenceScore', label: 'Confidence', width: 100, format: 'number' },
  { field: 'createdAt', label: 'Received', width: 150, format: 'datetime', sortable: true },
];

// ─────────────────────────────────────────────────────────────────────────────
// Full Template Objects
// ─────────────────────────────────────────────────────────────────────────────

/**
 * HMS Job Detail template — record view with right assist panel for source
 * documents.  The assist panel is collapsible and defaults to closed.
 * It renders `PanelContentDSL.type: 'documents'` which maps to SourceDocPanel.
 */
export const HMS_JOB_DETAIL_TEMPLATE: ViewTemplateDSL = {
  // Modern layout (default)
  sections: HMS_JOB_DETAIL_SECTIONS,
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'record', surface: 'desktop-first' },
  panels: {
    assist: {
      enabled: true,
      side: 'right',
      mode: 'sliding-resizable',
      resizable: true,
      collapsible: true,
      defaultOpen: false,
      width: 'lg',
      content: { type: 'documents', title: '源文件' },
    },
  },
};

/**
 * HMS Job Detail — Classic layout (模仿老版 WebForms / ASPX 风格)
 *
 * 特点：
 *   - 2-column stacked layout，还原老用户熟悉的双栏宽版
 *   - 不使用 Assist Panel，所有字段平铺在主页面
 *   - density: 'compact' — 极高信息密度
 *   - Job Header、Container、Vessel & Route、Dates、Assignment、Cargo
 *     六个 Section 全部堆叠，无需点击侧边栏即可看到所有核心字段
 *   - CSS 层通过 `[data-design-mode="classic"]` 可覆盖状态颜色（USE=深蓝, CLS=灰）
 */
export const HMS_JOB_DETAIL_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  sections: [
    {
      title: 'Job Header',
      columns: 2,
      fields: [
        { field: 'jobNumber', label: 'Job No' },
        { field: 'jobType', label: 'Type' },
        { field: 'status', label: 'Status' },
        { field: 'customerName', label: 'Customer' },
        { field: 'customerRef', label: 'Customer Ref' },
        { field: 'doNo', label: 'D/O No' },
        { field: 'blNo', label: 'B/L No' },
      ],
    },
    {
      title: 'Container',
      columns: 2,
      fields: [
        { field: 'containerNo', label: 'Container No' },
        { field: 'containerSize', label: 'Size' },
        { field: 'sealNo', label: 'Seal No' },
        { field: 'terminal', label: 'Terminal' },
      ],
    },
    {
      title: 'Vessel & Route',
      columns: 2,
      fields: [
        { field: 'vessel', label: 'Vessel' },
        { field: 'voyage', label: 'Voyage' },
        { field: 'origin', label: 'Origin' },
        { field: 'destination', label: 'Destination' },
      ],
    },
    {
      title: 'Critical Dates',
      columns: 2,
      fields: [
        { field: 'eta', label: 'ETA' },
        { field: 'etd', label: 'ETD' },
        { field: 'lfd', label: 'Last Free Date (LFD)' },
        { field: 'dischargeDate', label: 'Discharge' },
        { field: 'availableDate', label: 'Available' },
        { field: 'pickupDate', label: 'Pickup' },
        { field: 'deliveryDate', label: 'Delivery' },
      ],
    },
    {
      title: 'Assignment',
      columns: 2,
      fields: [
        { field: 'driverName', label: 'Driver' },
        { field: 'driverPhone', label: 'Driver Phone' },
        { field: 'driverPlate', label: 'Vehicle' },
        { field: 'dispatcher', label: 'Dispatcher' },
      ],
    },
    {
      title: 'Cargo',
      columns: 2,
      fields: [
        { field: 'cargoDesc', label: 'Description', span: 2 },
        { field: 'cargoWeightKg', label: 'Weight (kg)' },
        { field: 'hsCode', label: 'HS Code' },
        { field: 'dgClass', label: 'DG Class' },
        { field: 'deliveryAddress', label: 'Delivery Address', span: 2 },
      ],
    },
  ],
  density: 'compact',
  layout: {
    desktopColumns: 2,
    mobileColumns: 2,
    pageMode: 'record',
    surface: 'compact-first',
  },
  // No panels — all content flat on main page, classic ASPX style
};

/**
 * HMS Job List — Classic layout (老版列表风格)
 *
 * - 不分 Column Set，显示所有关键字段
 * - 无 Assist Panel / Detail Dock（点击直接跳转全页详情）
 * - density: 'compact' — 对齐老版 ASPX 列表行高
 */
export const HMS_JOB_LIST_CLASSIC_TEMPLATE: ViewTemplateDSL = {
  columns: [
    { field: 'jobNumber',     label: 'Job No',    width: 120, sortable: true, pinned: 'left' },
    { field: 'jobType',       label: 'Type',      width: 80,  format: 'status' },
    { field: 'status',        label: 'Status',    width: 100, format: 'status', sortable: true },
    { field: 'containerNo',   label: 'Container', width: 140 },
    { field: 'containerSize', label: 'Size',      width: 60 },
    { field: 'sealNo',        label: 'Seal',      width: 110 },
    { field: 'terminal',      label: 'Terminal',  width: 90 },
    { field: 'customerName',  label: 'Customer',  width: 150 },
    { field: 'blNo',          label: 'B/L No',    width: 140 },
    { field: 'vessel',        label: 'Vessel',    width: 120 },
    { field: 'voyage',        label: 'Voyage',    width: 70 },
    { field: 'eta',           label: 'ETA',       width: 90,  format: 'date', sortable: true },
    { field: 'lfd',           label: 'LFD',       width: 90,  format: 'date', sortable: true },
    { field: 'driverName',    label: 'Driver',    width: 110 },
    { field: 'driverPlate',   label: 'Vehicle',   width: 90 },
    { field: 'pickupDate',    label: 'Pickup',    width: 90,  format: 'date' },
    { field: 'deliveryDate',  label: 'Delivery',  width: 90,  format: 'date' },
    { field: 'createdAt',     label: 'Created',   width: 140, format: 'datetime', pinned: 'right' },
  ],
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'desktop-first' },
  page: {
    title: 'Container Trucking',
    subtitle: 'FCL container haulage — import IMP+RET, export COL+EXP',
    searchPlaceholder: 'Job no / container / B/L / customer / driver...',
  },
  list: {
    kind: 'major-grid',
    lineMode: '1-line',
    sorting: 'server',
    paging: 'server',
    search: { enabled: true, placeholder: 'Job no / container / B/L / customer / driver...' },
    topFilters: [
      { key: 'jobType', label: 'Type', type: 'multi-select' },
      { key: 'status', label: 'Status', type: 'multi-select' },
    ],
    savedViews: false,
    rowActions: ['view', 'edit'],
  },
  // No assist panel / no detail dock — classic click-through to full page
};

/**
 * HMS Job List template — major-list with right detail dock.
 */
export const HMS_JOB_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: HMS_JOB_LIST_COLUMNS,
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'desktop-first' },
  page: {
    title: 'Container Trucking',
    subtitle: 'FCL container haulage — import IMP+RET, export COL+EXP',
    searchPlaceholder: 'Job no / container / B/L / customer / driver...',
  },
  list: {
    kind: 'major-grid',
    lineMode: '1|3',
    sorting: 'server',
    paging: 'server',
    search: { enabled: true, placeholder: 'Job no / container / B/L / customer / driver...' },
    topFilters: [
      { key: 'jobType', label: 'Type', type: 'multi-select' },
      { key: 'status', label: 'Status', type: 'multi-select' },
    ],
    columnSets: [
      {
        id: 'operations',
        label: 'Operations',
        description: 'Container, driver, and date control',
        visibleColumns: ['jobNumber', 'jobType', 'status', 'containerNo', 'containerSize', 'customerName', 'terminal', 'lfd', 'driverName', 'driverPlate', 'pickupDate', 'deliveryDate'],
      },
      {
        id: 'vessel',
        label: 'Vessel',
        description: 'Shipping info and routing',
        visibleColumns: ['jobNumber', 'status', 'containerNo', 'blNo', 'vessel', 'eta', 'etd', 'terminal', 'lfd'],
      },
    ],
    savedViews: true,
    rowActions: ['view', 'edit'],
  },
  panels: {
    main: { type: 'workspace-grid', frozenColumns: 1, allowInlineEdit: false },
    assist: {
      enabled: true,
      side: 'right',
      mode: 'sliding-resizable',
      resizable: true,
      collapsible: true,
      defaultOpen: false,
      width: 'lg',
      content: { type: 'documents', title: '源文件' },
    },
  },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click', 'action-button'],
    defaultViewMode: 'view',
    allowEdit: true,
    form: { desktopColumns: 6, mobileColumns: 2 },
    subLists: [
      { id: 'trips', label: 'Trips', slug: 'job-trips', queryParam: 'jobId', sourceField: 'id', editable: 'none', actions: ['open'] },
    ],
  },
};

/**
 * Intake Cases List template — batch review with right detail dock that
 * includes source document assist panel.
 */
export const INTAKE_CASES_LIST_TEMPLATE: ViewTemplateDSL = {
  columns: INTAKE_CASES_LIST_COLUMNS,
  density: 'compact',
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'major-list', surface: 'desktop-first' },
  page: {
    title: 'AI 收件箱',
    subtitle: '审批 AI 自动解析的邮件和消息',
    searchPlaceholder: '主题 / 发件人 / 类型...',
  },
  list: {
    kind: 'major-grid',
    lineMode: '1|3',
    sorting: 'server',
    paging: 'server',
    search: { enabled: true, placeholder: '主题 / 发件人 / 类型...' },
    topFilters: [
      { key: 'status', label: '状态', type: 'multi-select' },
      { key: 'channelType', label: '渠道', type: 'multi-select' },
    ],
    savedViews: false,
    rowActions: ['view'],
  },
  detailDock: {
    enabled: true,
    side: 'right',
    mode: 'dock-sheet',
    width: 'xl',
    openFrom: ['row-click'],
    defaultViewMode: 'view',
    allowEdit: false,
  },
  panels: {
    assist: {
      enabled: true,
      side: 'right',
      mode: 'sliding-resizable',
      resizable: true,
      collapsible: false,
      defaultOpen: true,
      width: 'lg',
      content: { type: 'documents', title: '源文件' },
    },
  },
};

/** Compact form template for HMS job (slug: hms-job-form). */
export const HMS_JOB_FORM_TEMPLATE: ViewTemplateDSL = {
  layout: { desktopColumns: 6, mobileColumns: 2, pageMode: 'record', surface: 'desktop-first' },
  fields: HMS_JOB_FORM_FIELDS,
};
